import {
  ExclamationTriangleIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/solid';
import { Button } from '@praetorian-chariot/ui';
import { fetchMFAPreference } from 'aws-amplify/auth';
import { isAfter } from 'date-fns';
import React, { useEffect, useMemo, useState } from 'react';

import { PasswordResetSection } from '@/components/PasswordResetSection';
import { Table } from '@/components/table/Table';
import { TwoFactorAuthSection } from '@/components/TwoFactorAuthSection';
import { useDeleteApiKey } from '@/hooks/useApiKey';
import { useGenericSearch } from '@/hooks/useGenericSearch';
import {
  apiKeyColumns,
  ApiKeyModal,
  ApiKeyWithExpired,
  DeleteApiKeyModal,
  SettingDisplayCard,
} from '@/sections/settings/components';

interface AuthenticationSettingsSubsectionProps {
  me: string;
  ssoDomain: string | undefined;
}

/**
 * AuthenticationSettingsSubsection handles security and access control settings.
 *
 * Contains:
 * - Authentication Method card: SSO vs Local authentication display
 * - Password card: Password reset (local auth only)
 * - Two-Factor Authentication card: MFA enrollment (local auth only)
 * - API Keys card: Personal API key management
 */
export const AuthenticationSettingsSubsection: React.FC<
  AuthenticationSettingsSubsectionProps
> = ({ me, ssoDomain }) => {
  // Local auth controls are disabled for SSO users
  const localAuthDisabled = !!ssoDomain;

  // 2FA status - fetched from AWS Cognito on mount for accurate badge display
  const [has2FA, setHas2FA] = useState<boolean | undefined>(undefined);

  // Fetch actual MFA status on component mount
  useEffect(() => {
    async function fetchMFAStatus() {
      // Skip for SSO users (they don't use Cognito MFA)
      if (ssoDomain) {
        setHas2FA(false);
        return;
      }

      try {
        const mfaPreference = await fetchMFAPreference();

        // Check if TOTP is enabled or preferred
        const totpEnabled =
          mfaPreference.preferred === 'TOTP' ||
          mfaPreference.enabled?.includes('TOTP');

        setHas2FA(totpEnabled);
      } catch (error) {
        console.error('Failed to fetch MFA preference:', error);
        // On error, assume no 2FA (safer default for badge display)
        setHas2FA(false);
      }
    }

    fetchMFAStatus();
  }, [ssoDomain]);

  // API Keys state
  const [isApiKeyModalOpen, setIsApiKeyModalOpen] = useState(false);
  const [confirmDeleteKey, setConfirmDeleteKey] = useState<{
    key: string;
    name: string;
  } | null>(null);

  // API Keys data with standard caching - always for logged-in user
  const {
    data: apiKeysData,
    status: apiKeysStatus,
    error: apiKeysError,
  } = useGenericSearch(
    {
      query: '#key',
    },
    {
      email: me, // Always use own email
      staleTime: 30000,
      gcTime: 300000,
    }
  );

  const { mutate: deleteApiKey, isPending: isDeletingKey } = useDeleteApiKey();

  // Process API keys data - add isExpired flag
  const apiKeys: ApiKeyWithExpired[] = useMemo(() => {
    return (apiKeysData?.keies || [])
      .filter(key => !key.deleted)
      .map(key => ({
        ...key,
        isExpired: key.expires
          ? isAfter(new Date(), new Date(key.expires))
          : false,
      }));
  }, [apiKeysData]);

  return (
    <>
      {/* Authentication Settings Subsection */}
      <h3
        id="auth-settings-heading"
        className="text-chariot-text-primary mt-8 text-base font-bold"
        data-testid="auth-settings-subsection-header"
      >
        Authentication Settings
      </h3>

      {/* Card 3: Authentication Method */}
      <SettingDisplayCard data-testid="authentication-method-card">
        <div className="flex w-full items-center justify-between">
          <div className="flex flex-col gap-1">
            <h4 className="font-bold">Authentication Method</h4>
            <span className="text-chariot-text-secondary text-xs">
              {ssoDomain
                ? 'Managed by your identity provider'
                : 'Email and password authentication'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {ssoDomain ? (
              <>
                <ShieldCheckIcon className="size-5 text-green-500" />
                <span className="text-chariot-text-primary font-bold">
                  SSO Authentication
                </span>
              </>
            ) : has2FA ? (
              <>
                <ShieldCheckIcon className="size-5 text-green-500" />
                <span className="text-chariot-text-primary font-bold">
                  Local + Two Factor
                </span>
              </>
            ) : (
              <>
                <ExclamationTriangleIcon className="size-5 text-red-500" />
                <span className="text-chariot-text-primary font-bold">
                  Local Authentication
                </span>
              </>
            )}
          </div>
        </div>
      </SettingDisplayCard>

      {/* Card 4: Password Reset (Always visible, disabled for SSO) */}
      <PasswordResetSection
        className="border-chariot-stroke bg-chariot-background-primary rounded-md border px-4 py-6"
        data-testid="password-card"
        disabled={localAuthDisabled}
        showLastChanged={true}
        buttonStyleType="primary"
        buttonSize="sm"
      />

      {/* Card 5: Two-Factor Authentication (Always visible, disabled for SSO) */}
      <TwoFactorAuthSection
        className="border-chariot-stroke bg-chariot-background-primary rounded-md border px-4 py-6"
        data-testid="mfa-card"
        variant="expanded"
        disabled={localAuthDisabled}
        showHeader={true}
        mfaEnabled={has2FA}
        onMfaEnabledChange={setHas2FA}
      />

      {/* Card 6: API Keys - Always render, table handles empty state */}
      <SettingDisplayCard data-testid="api-keys-card">
        <div className="flex w-full flex-col gap-4">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex flex-col gap-1">
              <h4 className="font-bold">API Keys</h4>
              <span className="text-chariot-text-secondary text-xs">
                {apiKeys.length} {apiKeys.length === 1 ? 'key' : 'keys'}{' '}
                configured
              </span>
            </div>
            <Button
              onClick={() => setIsApiKeyModalOpen(true)}
              label="Add Key"
              styleType="primary"
              size="xs"
              data-testid="new-api-key-button"
            />
          </div>

          {/* Table - always renders, shows empty state when no keys */}
          <div className="max-h-[400px] overflow-auto">
            <Table
              name="api-keys-table"
              columns={apiKeyColumns}
              data={apiKeys}
              status={apiKeysStatus}
              error={apiKeysError}
              selection={{ hide: true }}
              tableClassName="border border-chariot-stroke rounded-md bg-chariot-background-primary"
              hideTableHeader={false}
              loadingRowCount={5}
              isTableView={false}
              noData={{
                title: 'No API Keys',
                description: 'Create your first API key to get started',
              }}
              rowActions={item => ({
                menu: {
                  items: [
                    {
                      label: 'Delete API Key',
                      value: 'delete',
                      onClick: () =>
                        setConfirmDeleteKey({ key: item.key, name: item.name }),
                      disabled: isDeletingKey,
                    },
                  ],
                },
              })}
            />
          </div>
        </div>
      </SettingDisplayCard>

      {/* API Key Modals */}
      <ApiKeyModal
        open={isApiKeyModalOpen}
        onClose={() => setIsApiKeyModalOpen(false)}
      />
      <DeleteApiKeyModal
        open={!!confirmDeleteKey}
        apiKey={confirmDeleteKey}
        onConfirm={() => {
          if (!confirmDeleteKey) return;
          deleteApiKey(
            { key: confirmDeleteKey.key },
            {
              onSuccess: () => {
                setConfirmDeleteKey(null);
                // Toast handled by useDeleteApiKey hook
              },
              onError: () => {
                setConfirmDeleteKey(null);
                // Toast handled by useDeleteApiKey hook via defaultErrorMessage
              },
            }
          );
        }}
        onClose={() => setConfirmDeleteKey(null)}
        isPending={isDeletingKey}
      />
    </>
  );
};
